import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  downloadBlob,
  downloadProductExcelModel,
  exportProductsExcel,
  getEstabelecimentos,
  importProductsExcelBatch,
  validateProductsExcel,
} from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import PageHeader from '../common/PageHeader';

const FASE_LABEL = {
  Produtos: 'Produtos',
  CodigosAdicionais: 'Códigos adicionais',
  PrecosFiliais: 'Preços filiais',
  Concluido: 'Concluído',
};

export default function ProductExcelPage() {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [validacao, setValidacao] = useState(null);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [filiais, setFiliais] = useState([]);
  const [filialPrecosId, setFilialPrecosId] = useState('');

  const loadFiliais = useCallback(async () => {
    try {
      const { data } = await getEstabelecimentos();
      const lista = Array.isArray(data) ? data : [];
      setFiliais(lista);
      if (lista.length > 1) {
        setFilialPrecosId(String(lista[1].id));
      }
    } catch {
      setFiliais([]);
    }
  }, []);

  useEffect(() => {
    loadFiliais();
  }, [loadFiliais]);

  const run = async (action) => {
    setLoading(true);
    setError('');
    try {
      await action();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha na operação Excel.'));
    } finally {
      setLoading(false);
    }
  };

  const importarEmLotes = async (token) => {
    setImporting(true);
    setResult(null);
    setProgress(null);
    setError('');

    const errosAcumulados = [];
    let ultimo = null;

    try {
      for (;;) {
        const { data: lote } = await importProductsExcelBatch({ token, tamanhoLote: 40 });
        ultimo = lote;

        if (lote.errosNesteLote?.length) {
          lote.errosNesteLote.forEach((e) => {
            const key = `${e.aba}|${e.linha}|${e.mensagem}`;
            if (!errosAcumulados.some((x) => `${x.aba}|${x.linha}|${x.mensagem}` === key)) {
              errosAcumulados.push(e);
            }
          });
        }

        setProgress({
          fase: lote.fase,
          processadosGeral: lote.processadosGeral,
          totalGeral: lote.totalGeral,
          criados: lote.criados,
          atualizados: lote.atualizados,
          precosFiliaisAtualizados: lote.precosFiliaisAtualizados,
        });

        if (lote.concluido) break;
      }

      setResult({
        processados: ultimo?.processados ?? 0,
        criados: ultimo?.criados ?? 0,
        atualizados: ultimo?.atualizados ?? 0,
        precosFiliaisAtualizados: ultimo?.precosFiliaisAtualizados ?? 0,
        errosDetalhados: errosAcumulados,
      });
      setValidacao(null);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha na importação.'));
    } finally {
      setImporting(false);
      setProgress(null);
    }
  };

  const matrizId = filiais.length > 0 ? String(filiais[0].id) : '';
  const filiaisDestino = filiais.filter((f) => String(f.id) !== matrizId);
  const busy = loading || importing;
  const pctGeral =
    progress?.totalGeral > 0
      ? Math.min(100, Math.round((progress.processadosGeral / progress.totalGeral) * 100))
      : 0;

  const errosValidacao = validacao?.erros ?? [];
  const errosResultado = result?.errosDetalhados ?? [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Excel de produtos"
        subtitle={
          <>
            Aba principal: cadastro e preços da <strong>matriz</strong> (1ª filial).
            Aba <strong>PrecosFiliais</strong>: código + valor varejo por filial (coluna Filial
            opcional se escolher a filial abaixo).
          </>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <TextField
            select
            label="Filial destino (aba PrecosFiliais sem coluna Filial)"
            value={filialPrecosId}
            onChange={(e) => setFilialPrecosId(e.target.value)}
            fullWidth
            size="small"
            disabled={busy}
            helperText="Use quando a planilha tiver só Código e Valor na aba PrecosFiliais."
          >
            {filiaisDestino.map((f) => (
              <MenuItem key={f.id} value={String(f.id)}>
                {f.nomeFantasia || f.razaoSocial || `Filial ${f.id}`}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={busy}
              onClick={() =>
                run(async () => {
                  const response = await downloadProductExcelModel();
                  downloadBlob(response.data, 'ModeloProdutos.xlsx');
                })
              }
            >
              Baixar modelo
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={busy}
              onClick={() =>
                run(async () => {
                  const response = await exportProductsExcel();
                  downloadBlob(
                    response.data,
                    `CadastroProdutos_${new Date().toISOString().slice(0, 10)}.xlsx`,
                  );
                })
              }
            >
              Exportar cadastro
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              Selecionar Excel
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                run(async () => {
                  setValidacao(null);
                  setResult(null);
                  const response = await validateProductsExcel(file, {
                    filialPrecosId: filialPrecosId ? Number(filialPrecosId) : undefined,
                  });
                  setValidacao(response.data);
                });
              }}
            />
          </Stack>
        </Stack>
      </Paper>

      {(loading || importing) && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant={importing && progress?.totalGeral ? 'determinate' : 'indeterminate'} value={pctGeral} />
          {importing && progress && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {FASE_LABEL[progress.fase] || progress.fase} — {progress.processadosGeral} de{' '}
              {progress.totalGeral} ({pctGeral}%) · criados: {progress.criados} · atualizados:{' '}
              {progress.atualizados}
              {progress.precosFiliaisAtualizados > 0 && (
                <> · preços filiais: {progress.precosFiliaisAtualizados}</>
              )}
            </Typography>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {validacao && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Alert
            severity={validacao.token ? (errosValidacao.length ? 'warning' : 'success') : 'error'}
            sx={{ mb: 2 }}
          >
            {validacao.token ? (
              <>
                Arquivo lido com sucesso.
                <br />
                Produtos: {validacao.totalLinhasProdutos} linha(s) · válidas:{' '}
                {validacao.linhasValidasProdutos}
                {validacao.totalLinhasPrecosFiliais > 0 && (
                  <>
                    <br />
                    Preços filiais: {validacao.totalLinhasPrecosFiliais} linha(s)
                  </>
                )}
                {validacao.totalLinhasCodigosAdicionais > 0 && (
                  <>
                    <br />
                    Códigos adicionais: {validacao.totalLinhasCodigosAdicionais} linha(s)
                  </>
                )}
                {errosValidacao.length > 0 && (
                  <>
                    <br />
                    {errosValidacao.length} aviso(s) encontrado(s) — linhas com erro serão
                    ignoradas na importação.
                  </>
                )}
              </>
            ) : (
              <>Não foi possível ler o arquivo. Corrija os erros abaixo e tente novamente.</>
            )}
          </Alert>

          {errosValidacao.length > 0 && (
            <TableContainer sx={{ maxHeight: 320, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Aba</TableCell>
                    <TableCell width={80}>Linha</TableCell>
                    <TableCell>Mensagem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errosValidacao.map((erro, idx) => (
                    <TableRow key={`${erro.aba}-${erro.linha}-${idx}`}>
                      <TableCell>{erro.aba}</TableCell>
                      <TableCell>{erro.linha > 0 ? erro.linha : '—'}</TableCell>
                      <TableCell>{erro.mensagem}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {validacao.token && (
            <Button
              variant="contained"
              color="primary"
              disabled={busy}
              onClick={() => importarEmLotes(validacao.token)}
            >
              Importar {validacao.totalLinhasProdutos} produto(s)
            </Button>
          )}
        </Paper>
      )}

      {result && (
        <Paper sx={{ p: 2 }}>
          <Alert severity={errosResultado.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
            Importação concluída — criados: {result.criados} · atualizados: {result.atualizados}
            {result.precosFiliaisAtualizados != null && result.precosFiliaisAtualizados > 0 && (
              <>
                <br />
                Preços filiais atualizados: {result.precosFiliaisAtualizados}
              </>
            )}
            {errosResultado.length > 0 && (
              <>
                <br />
                {errosResultado.length} linha(s) com erro (demais linhas foram importadas).
              </>
            )}
          </Alert>
          {errosResultado.length > 0 && (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Aba</TableCell>
                    <TableCell width={80}>Linha</TableCell>
                    <TableCell>Mensagem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errosResultado.map((erro, idx) => (
                    <TableRow key={`${erro.aba}-${erro.linha}-${idx}`}>
                      <TableCell>{erro.aba}</TableCell>
                      <TableCell>{erro.linha > 0 ? erro.linha : '—'}</TableCell>
                      <TableCell>{erro.mensagem}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Box>
  );
}
